import { useState, useCallback } from 'react';

/*
 * SoloTrigger — every action that bumps a single-user reliability limit.
 * When fired, show the Solo modal.
 */
export const SoloTrigger = {
  SETLIST_LIMIT_REACHED:      'setlist_limit_reached',
  PDF_UPLOAD_LIMIT_REACHED:   'pdf_upload_limit_reached',
  SONG_LIBRARY_LIMIT_REACHED: 'song_library_limit_reached',
  OFFLINE_CACHE_LIMIT_REACHED:'offline_cache_limit_reached',
  LIVE_MODE_GATED:            'live_mode_gated',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type SoloTrigger = typeof SoloTrigger[keyof typeof SoloTrigger];

/*
 * BandTrigger — every action whose value depends on coordinating with other
 * people. When fired, show the Band modal.
 */
export const BandTrigger = {
  INVITE_MEMBER_CLICKED:       'invite_member_clicked',
  SHARE_SETLIST_CLICKED:       'share_setlist_clicked',
  ASSIGN_ROLE_CLICKED:         'assign_role_clicked',
  COLLABORATIVE_EDIT_DETECTED: 'collaborative_edit_detected',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BandTrigger = typeof BandTrigger[keyof typeof BandTrigger];

export interface UseUpgradeModalReturn {
  /** Open the Solo modal. Reason is forwarded to analytics when wired. */
  showSolo: (reason: SoloTrigger) => void;
  /** Open the Band modal. Reason is forwarded to analytics when wired. */
  showBand: (reason: BandTrigger) => void;
  /** Close whichever modal is currently open. */
  dismiss: () => void;
  /** Which modal (if any) is currently open. */
  currentModal: 'solo' | 'band' | null;
}

/*
 * Overlap rules enforced here:
 *  1. Only one modal open at a time — opening one replaces the other.
 *  2. Band always wins over Solo: if the Band modal is open, a Solo trigger
 *     will not replace it. Solo can always be re-shown after dismissal.
 */
export function useUpgradeModal(): UseUpgradeModalReturn {
  const [currentModal, setCurrentModal] = useState<'solo' | 'band' | null>(null);

  const showSolo = useCallback((_reason: SoloTrigger) => {
    // Band always wins — don't replace an open Band modal with Solo.
    setCurrentModal(prev => prev === 'band' ? 'band' : 'solo');
  }, []);

  const showBand = useCallback((_reason: BandTrigger) => {
    setCurrentModal('band');
  }, []);

  const dismiss = useCallback(() => {
    setCurrentModal(null);
  }, []);

  return { showSolo, showBand, dismiss, currentModal };
}
